import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '../ui/PageHeader';
import { TabPanel, Tabs } from '../ui/Tabs';
import { Newsletters } from '../newsletter/Newsletters';
import { AutomaticMails } from './AutomaticMails';
import { MailBrandSettings } from './MailBrandSettings';
import { Subscribers } from './Subscribers';
import { useMailSettings, type MailSettingsState } from './useMailSettings';
import { useSegments } from './useSegments';
import { useAdmin } from '../AdminContext';
import { useCan } from '../useCan';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { plural } from '../../core/format/text';

// De nieuwsbrief eerst: daar komt Sarah elke week; de automatische mails stel je één keer in.
// Automatische mails en de afzender horen bij de instellingen (Marketing ziet ze niet).
const allTabs = [{
  key: 'nieuwsbrief',
  label: 'Nieuwsbrief'
}, {
  key: 'automatisch',
  label: 'Automatische mails',
  settings: true
}, {
  key: 'abonnees',
  label: 'Abonnees & doelgroepen'
}, {
  key: 'huisstijl',
  label: 'Huisstijl & afzender',
  settings: true
}] as const;
type TabKey = (typeof allTabs)[number]['key'];

/** Een stip achter het tabblad met niet-opgeslagen werk, zodat het niet uit beeld raakt. */
const unsaved = (key: TabKey, mailDirty: boolean, segmentsDirty: boolean) => (key === 'automatisch' || key === 'huisstijl') && mailDirty || key === 'abonnees' && segmentsDirty;

/** /be/mail — alles wat de deur uitgaat per mail, op één plek. */
export function MailHome() {
  const {
    canAll
  } = useCan();
  // Alleen ophalen wie de mailinstellingen mag zien: anders geen verzoek dat toch 403 geeft.
  return canAll('settings.edit') ? <MailHomeWithSettings data-id="element-2704" data-mp="1j9ajt3qzuedf" /> : <MailHomeView mail={null} data-id="element-2705" data-mp="1ja2gyj08ok5t" />;
}
function MailHomeWithSettings() {
  // Hier en niet in de tabbladen: zo blijft een concept staan als Sarah even naar een ander tabblad gaat.
  return <MailHomeView mail={useMailSettings()} data-id="element-2706" data-mp="3dmiix7m05c4v" />;
}
function MailHomeView({
  mail
}: {
  mail: MailSettingsState | null;
}) {
  const [params, setParams] = useSearchParams();
  const tabs = allTabs.filter(t => !('settings' in t) || mail);
  const tab: TabKey = tabs.find(t => t.key === params.get('tab'))?.key ?? 'nieuwsbrief';
  const segments = useSegments();
  const mailDirty = Boolean(mail?.dirty);
  const usesMailSettings = tab === 'automatisch' || tab === 'huisstijl';

  // Vanuit Inzicht › Klanten "Maak doelgroep": een voorgevulde doelgroep klaarzetten, niet
  // opslaan. Sarah ziet hem, kan hem aanpassen en slaat zelf op (dan telt de server de lezers).
  const prefilled = useRef(false);
  const {
    edit: editSegments,
    list: segmentList
  } = segments;
  useEffect(() => {
    if (prefilled.current || params.get('doelgroep') !== 'terugzien' || !segmentList) return;
    prefilled.current = true;
    const weeks = Math.min(104, Math.max(1, Math.round(Number(params.get('weken'))) || 8));
    editSegments(list => [...list, {
      id: `seg-${Date.now().toString(36)}`,
      name: `Niet gezien sinds ${weeks} weken`,
      rules: [{
        kind: 'customer',
        value: true
      }, {
        kind: 'lastVisitWeeks',
        weeks
      }]
    }]);
    setParams({
      tab: 'abonnees'
    }, {
      replace: true
    });
    toast('Doelgroep klaargezet', {
      description: 'Controleer hem en sla op; daarna telt de server hoeveel lezers erin vallen.'
    });
  }, [params, segmentList, editSegments, setParams]);
  return <div className={mailDirty ? 'pb-24' : ''} data-id="element-2707" data-mp="3j4m1ojegvg28">
      <PageHeader title="Mail & nieuwsbrief" description="Je nieuwsbrief, de mails die vanzelf gaan, wie er meeleest en hoe het eruitziet." data-id="element-2708" data-mp="3e8dm54wcxkuj" />
      <Tabs id="mail" label="Onderdeel" variant="pill" className="mb-6" tabs={tabs.map(t => unsaved(t.key, mailDirty, segments.dirty) ? {
      ...t,
      label: `${t.label} •`
    } : t)} value={tab} onChange={key => setParams({
      tab: key
    })} data-id="element-2709" data-mp="3syl3tiejizu6" />

      <TabPanel id="mail" value={tab} data-id="element-2710" data-mp="2vf07kacybr13">
        {tab === 'nieuwsbrief' && <Newsletters data-id="element-2711" data-mp="1yqsgds6ma60k" />}
        {tab === 'abonnees' && <Subscribers segments={segments} data-id="element-2712" data-mp="1ynwfpw3dx38f" />}
        {usesMailSettings && mail && <MailSettingsTab state={mail} tab={tab} data-id="element-2713" data-mp="23p297w4vcuj0" />}
      </TabPanel>

      {mail?.dirty && <MailSaveBar state={mail} data-id="element-2714" data-mp="27pm6r0tcu05o" />}
    </div>;
}
function MailSettingsTab({
  state,
  tab
}: {
  state: MailSettingsState;
  tab: 'automatisch' | 'huisstijl';
}) {
  if (state.status === 'error' && !state.settings) {
    return <LoadFailed title="De mails konden niet laden" onRetry={state.reload} data-id="element-2715" data-mp="2hfz1fluvv9pj" />;
  }
  if (!state.settings) return <LoadingLine label="Mails laden…" className="py-8 text-center" data-id="element-2716" data-mp="2mhelbalbra6c" />;
  return tab === 'automatisch' ? <AutomaticMails state={state} settings={state.settings} data-id="element-2717" data-mp="2rcae4cydy1ki" /> : <MailBrandSettings state={state} settings={state.settings} data-id="element-2718" data-mp="2veyo4nyu5ej2" />;
}

/** Eigen opslaan voor de mails: los van de conceptbalk, want het is een eigen blok op de server. */
function MailSaveBar({
  state
}: {
  state: MailSettingsState;
}) {
  // Staat de conceptbalk van het beheer er ook (telefoon), dan daarboven in plaats van eroverheen.
  const {
    dirty: adminDirty
  } = useAdmin();
  const bottom = adminDirty ? 'bottom-[calc(8.5rem+env(safe-area-inset-bottom))]' : 'bottom-[calc(4.5rem+env(safe-area-inset-bottom))]';
  return <div className={`fixed inset-x-3 ${bottom} z-nav lg:inset-x-auto lg:bottom-6 lg:right-6 lg:w-[28rem]`} data-id="element-2719" data-mp="jt67b6g9dhfq">
      <div role="region" aria-label="Mails opslaan" className="flex flex-wrap items-center gap-3 rounded-xl bg-ink px-4 py-3 text-shell shadow-lg" data-id="element-2720" data-mp="fsw2hx47cx2g">
        <p className="min-w-0 flex-1 text-[13px]" data-id="element-2721" data-mp="17hpjhx2fied3">
          {state.errors > 0 ? `Nog ${plural(state.errors, 'punt', 'punten')} op te lossen` : 'Mails niet opgeslagen'}
        </p>
        <button type="button" onClick={state.discard} className="min-h-[40px] rounded-full px-3 text-[13px] text-shell/80 hover:text-shell" data-id="element-2722" data-mp="yhj7kfmdpmg1">
          Verwerpen
        </button>
        <button type="button" onClick={state.save} disabled={state.saving || state.errors > 0} className="inline-flex min-h-[40px] items-center rounded-full bg-shell px-5 text-[14px] text-ink transition-colors duration-150 ease-soft hover:bg-white disabled:cursor-not-allowed disabled:opacity-50" data-id="element-2723" data-mp="3o4rzhp81nez3">
          {state.saving ? 'Opslaan…' : 'Opslaan'}
        </button>
      </div>
    </div>;
}